import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import DisciplineAnchor from '../discipline-anchor/discipline-anchor';
import { isRpcStateReady, RpcState, RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks'
import { DisciplineModel, DisciplinesType } from '@om-fe/models';
import { LocationBreadcrumbs } from './location-breadcrumbs';
import { CategoryBreadcrumbs } from './category-breadcrumbs';
import { ChildBreadcrumbs } from './child-breadcrumbs';
import { breadcrumbsBem, SCROLL_INTO_VIEW_OPTIONS } from './breadcrumbs.constants';
import { TournamentBreadcrumbs } from './tournament-breadcrumbs';
import { GetBreadcrumbsResponse } from '../../common/breadcrumbsResponse';

interface DisciplineBreadcrumbsV2PublicProps {
    readonly disciplineId: string;
    readonly isLarge?: boolean;
    readonly routeType: DisciplineRouteType;
    readonly locationId?: string;
    readonly tournamentId?: string;
    readonly categoryId?: string;
    readonly lang: string;
    readonly eventId?: string;
    readonly sportChildId?: string;
    readonly isLive?: boolean;
}

export type DisciplineBreadcrumbsProps = DisciplineBreadcrumbsV2PublicProps;

export const DisciplineBreadcrumbs: FC<DisciplineBreadcrumbsProps> = memo(function DisciplineBreadcrumbsComponent(
    {
        disciplineId,
        isLarge,
        routeType,
        locationId,
        lang,
        tournamentId,
        eventId,
        categoryId,
        sportChildId,
        isLive,
    }: DisciplineBreadcrumbsProps,
) {
    const params = useMemo(() => ({ sportId: disciplineId, lang }), [lang, disciplineId]);
    const [response] = useRpcState<GetBreadcrumbsResponse<DisciplineModel>>('disciplines', params);
    const ref: React.MutableRefObject<HTMLSpanElement | null> = useRef<HTMLSpanElement>(null);

    const { status }: RpcState<GetBreadcrumbsResponse<DisciplineModel>> = response;

    useEffect(() => {
        if (
            status === RpcStateStatus.Success
            && (!locationId || locationId === '0')
            && (!categoryId || categoryId === '0')
            && (!sportChildId || sportChildId === '0')
            && ref.current
        ) {
            ref.current.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);
        }
    }, [categoryId, locationId, sportChildId, status]);

    if (!isRpcStateReady(response)) {
        return null;
    }

    const { data } = response;
    const disciplineData: DisciplineModel | undefined = data.records[0];
    const disciplineName: string = disciplineData?.name || disciplineId;
    const parentId: string | undefined = disciplineData?.parentId;

    if (
        (locationId && locationId !== '0')
        || (categoryId && categoryId !== '0')
        || (sportChildId && sportChildId !== '0')
        || (parentId !== DisciplinesType.ElectronicSports && parentId !== DisciplinesType.Fighting && parentId)
    ) {
        const isCategoryType: boolean = routeType === DisciplineRouteType.Category;
        const isLocationType: boolean = routeType === DisciplineRouteType.Location;
        const isParentType: boolean = routeType === DisciplineRouteType.Parent;
        const parentName: string | undefined = disciplineData?.parentName;
        const useDiscipline: boolean = (parentId === DisciplinesType.ElectronicSports || parentId === DisciplinesType.Fighting) ? true : false;

        const disciplineIdValue: string = useDiscipline ? disciplineId : parentId || disciplineId;
        const disciplineNameValue: string = useDiscipline
            ? disciplineName
            : parentName || disciplineName;
        const childIdValue: string | undefined = useDiscipline
            ? undefined
            : parentId && disciplineId;
        const childNameValue: string | undefined = useDiscipline
            ? undefined
            : parentId && disciplineName;

        return (
            <>
                <DisciplineAnchor
                    title={disciplineName}
                    disciplineId={disciplineIdValue}
                    disciplineName={disciplineNameValue}
                    className={breadcrumbsBem('CurrentItem', 'Discipline')}
                    disciplineRouteType={routeType}
                    childId={childIdValue}
                    childName={childNameValue}
                    isLarge={isLarge}
                    isLive={isLive}
                >
                    <span className={breadcrumbsBem('Text', 'Discipline')}>{disciplineName}</span>
                </DisciplineAnchor>

                {
                    parentId !== DisciplinesType.Fighting && parentId !== DisciplinesType.ElectronicSports && (
                        <span className={breadcrumbsBem({
                            element: 'Separator',
                            modifiers: 'Tournament',
                        })}>&#47;
                        </span>
                    )
                }
                {
                    locationId && isLocationType && parentId !== DisciplinesType.Fighting && parentId !== DisciplinesType.ElectronicSports && (
                        <LocationBreadcrumbs
                            disciplineId={disciplineId}
                            disciplineName={disciplineName}
                            locationId={locationId}
                            routeType={routeType}
                            isLarge={isLarge}
                            lang={lang}
                            tournamentId={tournamentId}
                            eventId={eventId}
                            isLocationType={isLocationType}
                            isCategoryType={isCategoryType}
                            parentId={parentId}
                            isLive={isLive}
                        />
                    )
                }
                {
                    categoryId && isCategoryType && (
                        <CategoryBreadcrumbs
                            disciplineId={disciplineId}
                            disciplineName={disciplineName}
                            categoryId={categoryId}
                            routeType={routeType}
                            isLocationType={isLocationType}
                            isCategoryType={isCategoryType}
                            isLarge={isLarge}
                            lang={lang}
                            tournamentId={tournamentId}
                            eventId={eventId}
                            isLive={isLive}
                        />
                    )
                }
                {
                    sportChildId && isParentType && (
                        <ChildBreadcrumbs
                            disciplineName={disciplineName}
                            disciplineId={disciplineId}
                            sportChildId={sportChildId}
                            routeType={routeType}
                            isLarge={isLarge}
                            lang={lang}
                            tournamentId={tournamentId}
                            eventId={eventId}
                            isLocationType={isLocationType}
                            isCategoryType={isCategoryType}
                            isLive={isLive}
                        />
                    )
                }
                {
                    parentId && isParentType && tournamentId && (
                        <TournamentBreadcrumbs
                            lang={lang}
                            tournamentId={tournamentId}
                            isLarge={isLarge}
                            disciplineId={disciplineId}
                            disciplineName={disciplineName}
                            isLocationType={isLocationType}
                            isCategoryType={isCategoryType}
                            eventId={eventId}
                            isLive={isLive}
                        />
                    )
                }
            </>
        );
    }

    return (
        <span
            className={breadcrumbsBem(isLarge ? 'Text--Discipline' : 'CurrentItem--Discipline')}
            ref={ref}
        >
            {disciplineName}
        </span>
    );
});
